import React, { useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import { IconSpinner, LayoutComposer } from '../../components/index.js';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';

import { validProps } from './Field';


const SectionBuilder = loadable(() => import(/* webpackChunkName: "SectionBuilder" */ './SectionBuilder/SectionBuilder'));
const StaticPage = loadable(() => import(/* webpackChunkName: "StaticPage" */ './StaticPage'));

import css from './PageBuilder.module.css';

const MobileBottomNavbar = loadable(() => import(/* webpackChunkName: "MobileBottomNavbar" */ '../../components/MobileBottomNavbar/MobileBottomNavbar'));


const getMetadata = (meta, schemaType, fieldOptions) => {
  const { pageTitle, pageDescription, socialSharing } = meta;

  // pageTitle is used for <title> tag in addition to page schema for SEO
  const title = validProps(pageTitle, fieldOptions)?.content;
  // pageDescription is used for different <meta> tags in addition to page schema for SEO
  const description = validProps(pageDescription, fieldOptions)?.content;
  // Data used when the page is shared in social media services
  const openGraph = validProps(socialSharing, fieldOptions);
  // We add OpenGraph image as schema image if it exists.
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(
    schemaType
  );
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org (This template uses JSON-LD format)
  //
  // In addition to this schema data for search engines, src/components/Page/Page.js adds some extra schemas
  // Read more about schema:
  // - https://schema.org/
  // - https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data
  const pageSchemaForSEO = {
    '@context': 'http://schema.org',
    '@type': schemaType || 'WebPage',
    description: description,
    name: title,
    ...schemaHeadlineMaybe,
    ...schemaImageMaybe,
  };

  return {
    title,
    description,
    schema: pageSchemaForSEO,
    socialSharing: openGraph,
  };
};

const LoadingSpinner = () => {
  return (
    <div className={css.loading}>
      <IconSpinner delay={600} />
    </div>
  );
};

//////////////////
// Page Builder //
//////////////////

/**
 * PageBuilder can be used to build content pages using page-asset.json.
 *
 * Note: props can include a lot of things that depend on
 * - pageAssetsData: json asset that contains instructions how to build the page content
 *   - asset should contain an array of _sections_, which might contain _fields_ and an array of _blocks_
 *     - _blocks_ can also contain _fields_
 * - fallbackPage: component. If asset loading fails, this is used instead.
 * - options: extra mapping of 3 level of sub components
 *   - sectionComponents: { ['my-section-type']: { component: MySection } }
 *   - blockComponents: { ['my-component-type']: { component: MyBlock } }
 *   - fieldComponents: { ['my-field-type']: { component: MyField, pickValidProps: data => Number.isInteger(data.content) ? { content: data.content } : {} }
 *     - fields have this pickValidProps as an extra requirement for data validation.
 * - pageProps: props that are passed to src/components/Page/Page.js component
 *
 * @param {Object} props
 * @returns page component
 */
const PageBuilder = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    fallbackPage,
    schemaType,
    options,
    currentPage,
    notShowDesktopSearchBar,
    pageId,
    // New props to control staged rendering of sections
    stagedRender = false,
    stagedInitialCount = 1,
    ...pageProps
  } = props;

  if (!pageAssetsData && fallbackPage && !inProgress && error) {
    return fallbackPage;
  }

  // Page asset contains UI info and metadata related to it.
  // - "sections" (data that goes inside <body>)
  // - "meta" (which is data that goes inside <head>)
  const { sections = [], meta = {} } = pageAssetsData || {};
  // Staged rendering: show only the first N sections at first, then reveal all when scrolled to Nth
  const [showAllSections, setShowAllSections] = useState(!stagedRender);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!stagedRender) return;

    // If no window (SSR), show all to avoid hydration mismatch
    if (typeof window === 'undefined') {
      setShowAllSections(true);
      return;
    }

    // Use IntersectionObserver to detect when the user reaches the bottom of the last initially visible section
    const el = sentinelRef.current;
    if (!el) return;

    const reveal = () => setShowAllSections(true);
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          reveal();
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.2,
      }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [stagedRender, sections, stagedInitialCount]);

  const initialCount = Math.max(1, Number(stagedInitialCount) || 1);
  const visibleSections = showAllSections ? sections : sections.slice(0, initialCount);
  const pageMetaProps = getMetadata(meta, schemaType, options?.fieldComponents);

  const layoutAreas = `
    topbar
    main
    footer
  `;
  return (
    <StaticPage {...pageMetaProps} {...pageProps}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer
                  currentPage={currentPage}
                  notShowDesktopSearchBar={notShowDesktopSearchBar}
                  pageAssetsData={pageAssetsData}
                  pageId={pageId}
                />
              </Topbar>
              <Main as="main" className={css.main}>
                {visibleSections.length === 0 && inProgress ? (
                  <LoadingSpinner />
                ) : (
                  <SectionBuilder sections={visibleSections} options={options} />
                )}
                {/* Sentinel to trigger revealing remaining sections when scrolled to the end of initially visible content */}
                {stagedRender && !showAllSections && sections.length > initialCount ? (
                  <div ref={sentinelRef} aria-hidden="true" style={{ height: 1, width: '100%' }} />
                ) : null}
              </Main>
              <MobileBottomNavbar />
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export { LayoutComposer, StaticPage, SectionBuilder };

export default PageBuilder;
